import type { WikiLinkDialogState } from '../store/editor-store'
import { useEffect, useRef, useState } from 'react'
import { buildWikiLinkRaw } from '../extensions/wiki-link'
import { useEditor } from '../store/context'

interface SearchItem { id: number, label: string, value: string }

export function WikiLinkDialog() {
  const { store } = useEditor()

  const dialogRef = useRef<HTMLDialogElement>(null)
  const pageInputRef = useRef<HTMLInputElement>(null)

  const [page, setPage] = useState('')
  const [anchor, setAnchor] = useState('')
  const [display, setDisplay] = useState('')
  const [project, setProject] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [suggestionUrl, setSuggestionUrl] = useState('')
  const [searchResults, setSearchResults] = useState<SearchItem[]>([])
  const [showResults, setShowResults] = useState(false)

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const savedRangeRef = useRef<{ from: number, to: number } | undefined>(undefined)

  useEffect(() => {
    store.subscribeWikiLinkDialog((state: WikiLinkDialogState) => {
      setPage(state.page)
      setAnchor(state.anchor ?? '')
      setDisplay(state.display ?? '')
      setProject(state.project ?? '')
      setIsEditing(state.isEditing)
      setSuggestionUrl(state.suggestionUrl)
      savedRangeRef.current = state.range
      setSearchResults([])
      setShowResults(false)
      dialogRef.current!.showModal()
      requestAnimationFrame(() => pageInputRef.current?.focus())
    })
    // eslint-disable-next-line react/exhaustive-deps
  }, [])

  function close() {
    setShowResults(false)
    dialogRef.current!.close()
  }

  async function searchPages(query: string) {
    const url = suggestionUrl
    if (!url)
      return
    clearTimeout(debounceTimerRef.current)
    if (!query.trim()) {
      setSearchResults([])
      setShowResults(false)
      return
    }
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(url + encodeURIComponent(query))
        if (!res.ok)
          return
        const data: SearchItem[] = await res.json()
        setSearchResults(data)
        setShowResults(data.length > 0)
      }
      catch { /* ignore */ }
    }, 200)
  }

  function handlePageInput(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value
    setPage(val)
    void searchPages(val)
  }

  function selectResult(item: SearchItem) {
    setPage(item.value)
    setSearchResults([])
    setShowResults(false)
    pageInputRef.current?.focus()
  }

  function buildAttrs() {
    const p = page.trim()
    const proj = project.trim() || null
    const anch = anchor.trim() || null
    const disp = display.trim() || null
    const raw = buildWikiLinkRaw({ page: p, project: proj, anchor: anch, display: disp })
    return { raw, page: p, anchor: anch, project: proj }
  }

  function apply() {
    if (!page.trim())
      return
    const ed = store.editor()
    const attrs = buildAttrs()
    const displayText = display.trim() || page.trim()
    const range = savedRangeRef.current

    if (isEditing && range) {
      // Replace the marked text range with the updated display text + mark
      ed.chain().focus().insertContentAt(range, {
        type: 'text',
        text: displayText,
        marks: [{ type: 'wikiLink', attrs }],
      }).run()
    }
    else if (!isEditing && range && range.from !== range.to) {
      ed.chain().focus().deleteRange(range).insertContentAt(range.from, {
        type: 'text',
        text: displayText,
        marks: [{ type: 'wikiLink', attrs }],
      }).run()
    }
    else {
      ed.chain().focus().insertContent({
        type: 'text',
        text: displayText,
        marks: [{ type: 'wikiLink', attrs }],
      }).run()
    }
    close()
  }

  return (
    <dialog
      ref={dialogRef}
      className="tiptap-dialog"
      onClick={(e) => {
        if (e.target === dialogRef.current)
          close()
      }}
    >
      <p className="dialog-title">{isEditing ? 'Edit wiki link' : 'Insert wiki link'}</p>

      <div className="field">
        <label className="field-label">Page</label>
        <div className="wiki-link-search-wrapper">
          <input
            ref={pageInputRef}
            className="field-input"
            type="text"
            placeholder="Page name"
            value={page}
            onChange={handlePageInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && page.trim())
                apply()
            }}
            onFocus={() => {
              if (page.trim())
                void searchPages(page)
            }}
            onBlur={() => setTimeout(setShowResults, 150, false)}
          />
          {showResults && (
            <ul className="wiki-link-search-results">
              {searchResults.map(item => (
                <li
                  key={item.id}
                  className="wiki-link-search-item"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    selectResult(item)
                  }}
                >
                  {item.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="field">
        <label className="field-label">
          Anchor
          <span className="field-label-optional">(optional)</span>
        </label>
        <input
          className="field-input"
          type="text"
          placeholder="heading-id"
          value={anchor}
          onChange={e => setAnchor(e.target.value)}
        />
      </div>

      <div className="field">
        <label className="field-label">
          Display text
          <span className="field-label-optional">(optional)</span>
        </label>
        <input
          className="field-input"
          type="text"
          placeholder="Link label"
          value={display}
          onChange={e => setDisplay(e.target.value)}
        />
      </div>

      <div className="field">
        <label className="field-label">
          Project
          <span className="field-label-optional">(optional)</span>
        </label>
        <input
          className="field-input"
          type="text"
          placeholder="project-identifier"
          value={project}
          onChange={e => setProject(e.target.value)}
        />
      </div>

      <div className="dialog-actions">
        <button type="button" className="btn btn-cancel" onClick={close}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={!page.trim()} onClick={apply}>
          {isEditing ? 'Update' : 'Insert'}
        </button>
      </div>
    </dialog>
  )
}
